import { Badge, Button, Card, Checkbox, type BadgeTone } from '@wundo/ui';
import type { NotificationChannel } from '@wundo/shared/notificationChannel.ts';
import type {
  NotificationOutcome,
  NotificationRecipient,
  NotificationSkipReason,
} from '@/lib/notificationSend.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';
import type { SendReach } from './sendReach.ts';
import { CHANNEL_LABELS, nameOf, skipLabel } from './sendLabels.ts';

/* A bounce or a spam report is what staff must act on before mailing again. */
const OUTCOMES: Record<NotificationOutcome, { label: string; tone: BadgeTone }> = {
  delivered: { label: 'Delivered', tone: 'neutral' },
  bounced: { label: 'Bounced', tone: 'danger' },
  complained: { label: 'Marked as spam', tone: 'danger' },
};

interface PeopleListProps {
  people: NotificationRecipient[];
  isUserScope: boolean;
  isEmail: boolean;
  selectedIds: string[];
  /** Ticked people who are on the list, as opposed to every ticked id. */
  selectedCount: number;
  allSelected: boolean;
  reach: SendReach;
  canSend: boolean;
  onToggleAll: () => void;
  onToggleOne: (userId: string, checked: boolean) => void;
  onPreview: (userId: string) => void;
  onSend: () => void;
  onPick: (person: NotificationRecipient) => void;
  onResubscribe: (person: NotificationRecipient) => void;
}

/*
 * A user moment goes to the whole list at once, so the list is ticked off
 * rather than picked from, and every row says whether this person has had it.
 */
export function PeopleList(props: PeopleListProps) {
  const { people, isUserScope, reach } = props;

  return (
    <>
      {isUserScope && (
        <BulkToolbar
          selectedIdCount={props.selectedIds.length}
          selectedCount={props.selectedCount}
          allSelected={props.allSelected}
          reachableCount={reach.reachableCount}
          canSend={props.canSend}
          onToggleAll={props.onToggleAll}
          onSend={props.onSend}
        />
      )}

      {isUserScope && reach.leftOut.length > 0 && <LeftOutCard reach={reach} />}

      <ul className='max-h-72 divide-y divide-line overflow-y-auto rounded-md border border-line'>
        {people.map((person) => (
          <li key={person.user_id}>
            {isUserScope ? (
              <TickedPersonRow
                person={person}
                checked={props.selectedIds.includes(person.user_id)}
                isEmail={props.isEmail}
                skipReason={reach.skipReasonOf(person)}
                channel={reach.channelOf(person)}
                channelTone={reach.channelTone(person)}
                onCheckedChange={(checked) => props.onToggleOne(person.user_id, checked)}
                onPreview={() => props.onPreview(person.user_id)}
                onResubscribe={() => props.onResubscribe(person)}
              />
            ) : (
              <PickablePersonRow person={person} onPick={() => props.onPick(person)} />
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

interface BulkToolbarProps {
  selectedIdCount: number;
  selectedCount: number;
  allSelected: boolean;
  reachableCount: number;
  canSend: boolean;
  onToggleAll: () => void;
  onSend: () => void;
}

/* The send button sits on the list, where the number it goes to is. */
function BulkToolbar({
  selectedIdCount,
  selectedCount,
  allSelected,
  reachableCount,
  canSend,
  onToggleAll,
  onSend,
}: BulkToolbarProps) {
  return (
    <div className='flex items-center justify-between gap-3'>
      <div className='flex items-center gap-3'>
        <Button variant='quiet' size='sm' onClick={onToggleAll}>
          {allSelected ? 'Select none' : 'Select all'}
        </Button>
        <span className='text-sm text-ink-muted'>
          {selectedIdCount} selected
          {selectedCount > 0 && reachableCount === 0 && ' · none of them can receive it'}
        </span>
      </div>
      <Button disabled={!canSend} onClick={onSend}>
        Send to {selectedIdCount} selected
      </Button>
    </div>
  );
}

function LeftOutCard({ reach }: { reach: SendReach }) {
  return (
    <Card role='status' tone='quiet' className='border-danger-line'>
      <p className='text-sm font-semibold text-ink'>
        Left out when you send ({reach.leftOut.length})
      </p>
      <ul className='mt-2 list-inside list-disc text-sm text-ink-secondary'>
        {reach.leftOut.map((person) => (
          <li key={person.user_id}>
            {nameOf(person)} — {skipLabel(reach.skipReasonOf(person)!, person)}
          </li>
        ))}
      </ul>
    </Card>
  );
}

interface TickedPersonRowProps {
  person: NotificationRecipient;
  checked: boolean;
  isEmail: boolean;
  skipReason: NotificationSkipReason | null;
  channel: NotificationChannel;
  /** Red when that channel cannot reach them. */
  channelTone: BadgeTone;
  onCheckedChange: (checked: boolean) => void;
  onPreview: () => void;
  onResubscribe: () => void;
}

function TickedPersonRow({
  person,
  checked,
  isEmail,
  skipReason,
  channel,
  channelTone,
  onCheckedChange,
  onPreview,
  onResubscribe,
}: TickedPersonRowProps) {
  const name = nameOf(person);

  return (
    <div className='flex items-center gap-3 px-4 py-2'>
      <Checkbox
        aria-label={`Select ${name}`}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-sm font-semibold text-ink'>{name}</span>
        <span className='block truncate text-sm text-ink-muted'>
          {person.email ?? 'No email address'}
          {person.organisation_name ? ` · ${person.organisation_name}` : ''}
        </span>
        {skipReason && (
          <span className='block text-sm text-danger'>
            Left out: {skipLabel(skipReason, person)}
          </span>
        )}
      </span>
      <span className='flex shrink-0 flex-wrap items-center justify-end gap-1'>
        {person.sent_at && (
          <Badge tone='success'>
            {isEmail ? 'Last sent' : 'Sent'} {formatDateTime(person.sent_at)}
          </Badge>
        )}
        {person.last_outcome && (
          <Badge tone={OUTCOMES[person.last_outcome].tone}>
            {OUTCOMES[person.last_outcome].label}
          </Badge>
        )}
        <Badge tone={channelTone}>{CHANNEL_LABELS[channel]}</Badge>
        <Button
          variant='quiet'
          size='sm'
          aria-label={`Preview for ${name}`}
          onClick={onPreview}
        >
          Preview
        </Button>
        {channel === 'email' && person.email_opt_out_at && (
          <Button
            variant='quiet'
            size='sm'
            aria-label={`Resubscribe ${name}`}
            onClick={onResubscribe}
          >
            Resubscribe
          </Button>
        )}
      </span>
    </div>
  );
}

function PickablePersonRow({
  person,
  onPick,
}: {
  person: NotificationRecipient;
  onPick: () => void;
}) {
  return (
    <Button
      variant='ghost'
      shape='block'
      fullWidth
      className='justify-between gap-3 rounded-none px-4 py-2 text-left font-normal whitespace-normal'
      onClick={onPick}
    >
      <span className='min-w-0'>
        <span className='block truncate text-sm font-semibold text-ink'>
          {nameOf(person)}
        </span>
        <span className='block truncate text-sm text-ink-muted'>
          {person.email ?? 'No email address'}
          {person.organisation_name ? ` · ${person.organisation_name}` : ''}
        </span>
      </span>
      <Badge tone={person.notification_channel === 'none' ? 'neutral' : 'info'}>
        {CHANNEL_LABELS[person.notification_channel]}
      </Badge>
    </Button>
  );
}
